/* eslint-disable max-len */
import {
  Alert,
  Box,
  CardContent,
  FormControl,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  getFormularioPoliticaMunicipalAgriculturaFamiliarByUUID
} from 'api/forms/pacote_formularios.api';
import ContainerPainel from 'components/container_painel';
import { Loading } from 'components/login';
import DocumentComponent from 'components/shared/document_component';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormularioPoliticaMunicipalAgriculturaFamiliarStore } from '../../../../../stores/forms/FormularioPMAF.store';
import { ROUTES } from 'routes/constants';
import TextFieldDataCustom from 'components/shared/textfiel_data_custom';
import { ContainerFormFormik } from 'components/shared/container_form_formik';
import ButtonForm from 'components/buttons/button-form';
import {
  TIPOS_INSTRUMENTO,
  temErro,
  isFormValid,
  computePoliticaValue,
  computePlanoValue,
  handlePossuiPoliticaChange,
  handlePossuiPlanoChange,
  fetchFormulario,
  salvarRascunhoHandler,
  enviarAprovacaoHandler,
} from './helpers';

const FormPMAFEditar: React.FC = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const {
    formulario,
    setFormulario,
    setPartialForm,
    salvarRascunho,
    enviarAprovacao
  } = useFormularioPoliticaMunicipalAgriculturaFamiliarStore();

  const [loading, setLoading] = useState(true);


  const getFormulario = async () => {
    return fetchFormulario(uuid, getFormularioPoliticaMunicipalAgriculturaFamiliarByUUID, setFormulario, setLoading);
  };

  useEffect(() => {
    getFormulario();
    // eslint-disable-next-line
  }, []);

  if (loading) {
    return (
      <ContainerPainel
        pagina='Formulário da Política Municipal da Agricultura Familiar'
        showBackButton={true}
      >
        <Loading />
      </ContainerPainel>
    );
  }


  // CRUD para Gerente Municipal
  const possuiPolitica = formulario?.municipio_possui_politica_municipal_de_agricula_familiar_ou_indigena;
  const possuiPlano = formulario?.possui_plano_municipal_agricultura_familiar;

  const temErroLocal = (campo: string) => temErro(formulario, campo);
  const isFormValidLocal = () => isFormValid(formulario);
  const onPossuiPoliticaChange = (value: boolean) => handlePossuiPoliticaChange(setPartialForm, value);
  const onPossuiPlanoChange = (value: boolean) => handlePossuiPlanoChange(setPartialForm, value);

  const handleSalvarRascunho = async () => {
    return salvarRascunhoHandler(salvarRascunho, navigate, alert, ROUTES.PACOTE_FORMULARIOS);
  };

  const handleEnviarAprovacao = async () => {
    return enviarAprovacaoHandler(enviarAprovacao, () => isFormValid(formulario), navigate, alert, ROUTES.PACOTE_FORMULARIOS);
  };

  // valores derivados via helpers

  return (
    <ContainerPainel
      pagina='Formulário da Política Municipal da Agricultura Familiar'
      showBackButton={true}
    >
      <Stack marginX={'4rem'} paddingTop={'2rem'}>
        <Typography
          variant="h5"
          sx={{
            mt: 4,
            mb: 4,
            fontSize: '24px',
            fontWeight: 400,
            textAlign: 'center'
          }}
        >
          Política Municipal da Agricultura Familiar
        </Typography>

        <Typography
          variant="h5"
          sx={{
            fontSize: '16px',
            fontWeight: 400,
            textAlign: 'center'
          }}
        >
          O preenchimento deste formulário é feito em uma única etapa.
        </Typography>

        <Box mt={2}>
          <ContainerFormFormik
            title='Informações sobre a Política Municipal da Agricultura Familiar'
          //onSubmit={handleSubmit}
          >
            <CardContent sx={{ p: 4 }}>
              <Stack spacing={4}>
                <Alert severity="info">
                  O preenchimento deste formulário é feito em uma única etapa. Todos os campos obrigatórios devem ser preenchidos para salvar.
                </Alert>

                {/* Campo 1: O município possui Política Municipal de Agricultura Familiar? */}
                <FormControl component='fieldset' required error={temErroLocal('possui_politica')}>
                  <Typography
                    variant='subtitle1'
                    gutterBottom
                    sx={{
                      fontWeight: 'bold',
                      color: temErroLocal('possui_politica') ? 'error.main' : 'inherit'
                    }}
                  >
                    O município possui Política Municipal de Agricultura Familiar? *
                    {temErroLocal('possui_politica') && (
                      <Typography variant='caption' display='block' color='error'>
                        Este campo é obrigatório
                      </Typography>
                    )}
                  </Typography>
                  <RadioGroup
                    row
                    value={computePoliticaValue(formulario)}
                    onChange={(e) => onPossuiPoliticaChange(e.target.value === 'Sim')}
                  >
                    <FormControlLabel value='Sim' control={<Radio />} label='Sim' />
                    <FormControlLabel value='Não' control={<Radio />} label='Não' />
                  </RadioGroup>
                </FormControl>

                {/* Campos condicionais - só aparecem se "Sim" */}
                {possuiPolitica === true && (
                  <>
                    {/* Campo 2: Tipo do Instrumento/Dispositivo Legal */}
                    <Box>
                      <Typography
                        variant='subtitle1'
                        gutterBottom
                        sx={{
                          fontWeight: 'bold',
                          color: temErroLocal('tipo_instrumento') ? 'error.main' : 'inherit'
                        }}
                      >
                        Qual o Tipo do Instrumento/Dispositivo Legal? *
                        {temErroLocal('tipo_instrumento') && (
                          <Typography variant='caption' display='block' color='error'>
                            Este campo é obrigatório
                          </Typography>
                        )}
                      </Typography>
                      <FormControl fullWidth required error={temErroLocal('tipo_instrumento')}>
                        <Select
                          value={formulario?.tipo_de_instrumento ?? ''}
                          onChange={(e) => setPartialForm('tipo_de_instrumento', e.target.value)}
                          displayEmpty
                        >
                          <MenuItem value='' disabled>
                            Selecione uma opção
                          </MenuItem>
                          {TIPOS_INSTRUMENTO.map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>

                    {/* Campo 3: Número da publicação */}
                    <Box>
                      <Typography
                        variant='subtitle1'
                        gutterBottom
                        sx={{
                          fontWeight: 'bold',
                          color: temErroLocal('numero_publicacao') ? 'error.main' : 'inherit'
                        }}
                      >
                        Número da publicação do instrumento legal que instituiu a Política *
                        {temErroLocal('numero_publicacao') && (
                          <Typography variant='caption' display='block' color='error'>
                            Este campo é obrigatório
                          </Typography>
                        )}
                      </Typography>
                      <TextField
                        fullWidth
                        placeholder='Ex: nº da Lei'
                        value={formulario?.numero_da_publicacao ?? ''}
                        onChange={(e) => setPartialForm('numero_da_publicacao', e.target.value)}
                        required
                        error={temErroLocal('numero_publicacao')}
                      />
                    </Box>

                    {/* Campo 4: Data da publicação */}
                    <Box>
                      <Typography
                        variant='subtitle1'
                        gutterBottom
                        sx={{
                          fontWeight: 'bold',
                          color: temErroLocal('data_publicacao') ? 'error.main' : 'inherit'
                        }}
                      >
                        Data de publicação do instrumento legal que instituiu a Política *
                        {temErroLocal('data_publicacao') && (
                          <Typography variant='caption' display='block' color='error'>
                            Este campo é obrigatório
                          </Typography>
                        )}
                      </Typography>
                      <TextFieldDataCustom
                        label='Data de publicação'
                        type='date'
                        fullWidth
                        value={formulario?.data_da_publicacao ? formulario.data_da_publicacao.split('T')[0] : ''}
                        onChange={(e) => setPartialForm('data_da_publicacao', e.target.value)}
                        required
                        error={temErroLocal('data_publicacao')}
                        InputLabelProps={{
                          shrink: true,
                        }}
                      />
                    </Box>

                    {/* Campo 5: Upload de documento */}
                    <Box>
                      <Box sx={{
                        border: temErroLocal('upload_documento') ? '1px solid' : 'none',
                        borderColor: 'error.main',
                        borderRadius: '4px',
                        padding: temErroLocal('upload_documento') ? '8px' : '0'
                      }}>
                        <DocumentComponent
                          uuid={formulario?.upload_documento ?? null}
                          setUuid={(uuid) => setPartialForm('upload_documento', uuid)}
                          podeSubstituir={true}
                          title='Faça o upload do documento comprobatório'
                          subtitle='Para dar continuidade ao preenchimento, é necessário anexar o documento legal correspondente (como Decreto, Lei, Portaria ou outro normativo oficial).'
                        />
                      </Box>
                    </Box>
                  </>
                )}

                {/* Critério de Aceitação 5: Pergunta sobre o Plano Municipal da Agricultura Familiar */}
                <FormControl component='fieldset' required error={temErroLocal('possui_plano')}>
                  <Typography
                    variant='subtitle1'
                    gutterBottom
                    sx={{
                      fontWeight: 'bold',
                      color: temErroLocal('possui_plano') ? 'error.main' : 'inherit'
                    }}
                  >
                    O município possui o Plano Municipal da Agricultura Familiar – PMAF? *
                    {temErroLocal('possui_plano') && (
                      <Typography variant='caption' display='block' color='error'>
                        Este campo é obrigatório
                      </Typography>
                    )}
                  </Typography>
                  <RadioGroup
                    row
                    value={computePlanoValue(formulario)}
                    onChange={(e) => onPossuiPlanoChange(e.target.value === 'Sim')}
                  >
                    <FormControlLabel value="Sim" control={<Radio />} label="Sim" />
                    <FormControlLabel value="Não" control={<Radio />} label="Não" />
                  </RadioGroup>
                </FormControl>

                {/* Upload do Plano Municipal da Agricultura Familiar */}
                {possuiPlano === true && (
                  <Box>
                    <Box sx={{
                      border: temErroLocal('upload_plano') ? '1px solid' : 'none',
                      borderColor: 'error.main',
                      borderRadius: '4px',
                      padding: temErroLocal('upload_plano') ? '8px' : '0'
                    }}>
                      <DocumentComponent
                        uuid={formulario?.upload_plano_pmaf ?? null}
                        setUuid={(uuid) => setPartialForm('upload_plano_pmaf', uuid)}
                        podeSubstituir={true}
                        title='Faça o upload do Plano Municipal da Agricultura Familiar (PDF)'
                        subtitle='Para dar continuidade ao preenchimento, é necessário anexar o documento do Plano Municipal da Agricultura Familiar (PDF).'
                      />
                    </Box>
                  </Box>
                )}

                {/* Botões */}
                {/* <BottomActionButtons
                  handleSalvarRascunho={handleSalvarRascunho}
                  handleEnviarAprovacao={handleEnviarAprovacao}
                  isFormValid={isFormValidLocal()}
                  handleCancelar={() => navigate(-1)}
                /> */}
              </Stack>
            </CardContent>
          </ContainerFormFormik>

          <ButtonForm
            disabled={!isFormValidLocal()}
            onBack={() => navigate(-1)}
            onConfirmSave={handleEnviarAprovacao}
            onSaveDraft={handleSalvarRascunho}
          />
        </Box>
      </Stack>
    </ContainerPainel>
  );
};

export default FormPMAFEditar;
